import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Loader, Wrapper } from "../../components";
import {
  activePlansSummary,
  totalCreditsAndRemaining,
  creditAnalytics,
} from "../../services/creditsService";
import { Logs } from "../../services";
import { Link } from "react-router-dom";

export function Credit() {
  const [totalCredits, setTotalCredits] = useState(0);
  const [remainingCredits, setRemainingCredits] = useState(0);
  const [monthlyUsage, setMonthlyUsage] = useState(0);
  const [activePlansData, setActivePlansData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({});

  // 🔹 Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { user } = useSelector((state) => state.auth);

  if (!user || !user.company) {
    return (
      <Wrapper>
        <Loader />
      </Wrapper>
    );
  }

  const companyId = user.company.id;
  const jwtToken = user.jwt;

  useEffect(() => {
    if (!jwtToken) return;
  
    const fetchCreditBalanceData = async () => {
      try {
        setLoading(true);
  
        const activePlansSummaryRes = await activePlansSummary({ id: companyId });
        if (activePlansSummaryRes?.status === 200) {
          const rawPlans = Array.isArray(activePlansSummaryRes?.data?.data)
            ? activePlansSummaryRes.data.data
            : [];

          const normalizedPlans = rawPlans.map((plan) => ({
            id: plan?.id,
            ...plan?.attributes,
          }));

          setActivePlansData(normalizedPlans);
        }
  
        const creditAnalyticsRes = await creditAnalytics();
        if (creditAnalyticsRes?.status === 200) {
          const analyticsPayload =
            creditAnalyticsRes?.data?.data?.attributes ||
            creditAnalyticsRes?.data?.data ||
            creditAnalyticsRes?.data ||
            {};
          setAnalyticsData(analyticsPayload);
        }
  
        const totalCreditsAndRemainingRes = await totalCreditsAndRemaining(jwtToken);
        if (totalCreditsAndRemainingRes?.status === 200) {
          const creditData = totalCreditsAndRemainingRes?.data?.creditBalance;
          
          setTotalCredits(creditData?.totalCredits || 0);
          setRemainingCredits(creditData?.remainingCredits || 0);
          setMonthlyUsage(creditData?.currentUsedCredits || 0);
        }
      } catch (error) {
        Logs(error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchCreditBalanceData();
  }, [companyId, jwtToken]);

  // 🔹 Transactions mapping
  const apiUsageLogs =
    analyticsData?.apiUsageLogs ||
    analyticsData?.usageLogs ||
    analyticsData?.api_usage_logs ||
    [];

  const totalAssignedFromPlans = useMemo(
    () =>
      (Array.isArray(activePlansData) ? activePlansData : []).reduce(
        (sum, plan) => sum + Number(plan?.assignedCredit || 0),
        0
      ),
    [activePlansData]
  );

  const totalCurrentFromPlans = useMemo(
    () =>
      (Array.isArray(activePlansData) ? activePlansData : []).reduce(
        (sum, plan) => sum + Number(plan?.currentCredit || 0),
        0
      ),
    [activePlansData]
  );

  const analyticsTransactions = (Array.isArray(apiUsageLogs) ? apiUsageLogs : [])
    .map((log) => ({
      date: log?.date || log?.createdAt || "-",
      type: log?.type || "usage",
      description: log?.apiName || log?.api_name || "-",
      amount: Number(log?.consumption ?? log?.amount ?? 0),
      balance: log?.balance ?? log?.remainingCredits ?? "-",
      status: log?.status || "completed",
    }))
    .reverse();

  const assignedCreditTransactions = (Array.isArray(activePlansData) ? activePlansData : [])
    .map((plan) => {
      return {
        date: plan?.activationDate || plan?.createdAt || "-",
        type: "purchase",
        description: "Assigned Credits",
        amount: Number(plan?.assignedCredit || 0),
        balance: Number(plan?.currentCredit || 0),
        status: (plan?.status || "active").toLowerCase(),
      };
    })
    .reverse();

  const transactions =
    assignedCreditTransactions.length > 0 ? assignedCreditTransactions : analyticsTransactions;

  // 🔹 Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 🔹 Slice transactions for pagination
  const paginatedTransactions = transactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const typeColors = {
    usage: { bgcolor: "grey.100", color: "grey.700" },
    purchase: { bgcolor: "primary.main", color: "white" },
    refund: { bgcolor: "green.100", color: "green.700" },
  };

  const roundTo2 = (num) =>
    Math.round((Number(num) + Number.EPSILON) * 100) / 100;

  return (
    <Wrapper>
      {loading && <Loader />}
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={10} 
      sx={{"@media (max-width: 767px)": {
                    flexDirection: "column",
                    alignItems: "flex-start",
                    marginBottom: "15px",
                  },}}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, color: "#65758B", fontSize: "14px" }}
      >
        Monitor your API usage and manage credit balance
      </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/contact-us"
          sx={{
            marginLeft: "auto",
            borderRadius: "8px",
            fontSize: { xs: "12px", sm: "14px", md: "16px" },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 0.8, sm: 1, md: 1.2 },
            minWidth: { xs: "100px", sm: "120px", md: "140px" },

            "@media (max-width: 767px)": {
                    marginLeft: "inherit",
                  },
          }}
        >
          + Buy Credits
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mt: 1 }} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "2rem", mb: 1 }}>
                Available Credits
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#03A9F4", fontSize: "25px", mb: 1 }}>
                {analyticsData?.availableCredits?.currentMonth ?? totalCurrentFromPlans ?? remainingCredits}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: "1.4rem" }}>
                {analyticsData?.availableCredits?.percentageChange
                  ? `${analyticsData.availableCredits.percentageChange > 0 ? "+" : ""}${analyticsData.availableCredits.percentageChange}% from last month`
                  : "+12% from last month"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "2rem", mb: 1 }}>
                Monthly Usage
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#03A9F4", fontSize: "25px", mb: 1 }}>
                {roundTo2(analyticsData?.monthlyUsage?.currentMonth ?? monthlyUsage)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: "1.4rem" }}>
                {analyticsData?.monthlyUsage?.percentageChange
                  ? `${analyticsData.monthlyUsage.percentageChange > 0 ? "+" : ""}${analyticsData.monthlyUsage.percentageChange}% from last month`
                  : "-8% from last month"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "2rem", mb: 1 }}>
                Usage Limit
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#03A9F4", fontSize: "25px", mb: 1 }}>
                {analyticsData?.usageLimit?.usagePercentage || 0}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={analyticsData?.usageLimit?.usagePercentage || 0}
                sx={{ mt: 2, borderRadius: 2 }}
              />
              <Typography variant="caption" sx={{ fontSize: "1.4rem" }}>
                of {analyticsData?.usageLimit?.totalActiveCredits ?? totalAssignedFromPlans ?? totalCredits} total credits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Box sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "#E1E7EF", boxShadow: 1, mt: 5 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "20px", mb: 0.5, color: "#020817" }}>
          Recent Transactions
          </Typography>
          <Typography>Your latest credit transactions and API usage</Typography>
        </Box>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Date</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Type</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Description</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Amount</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Balance</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Status</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.length > 0 ? (
                paginatedTransactions.map((txn, idx) => (
                  <TableRow key={idx}>
                    <TableCell align="center" sx={{ fontSize: "1.3rem" }}>{txn.date}</TableCell>
                    <TableCell align="center">
                      <Chip label={txn.type} sx={{ ...(typeColors[txn.type] || typeColors.usage), fontSize: "1.3rem", px: 1 }} />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.3rem" }}>{txn.description}</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.3rem", fontWeight: 500, color: txn.amount <= 0 ? "success.main" : "error.main" }}>
                      {txn.amount}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.3rem" }}>{txn.balance}</TableCell>
                    <TableCell align="center">
                      <Chip label={txn.status} sx={{ bgcolor: "grey.100", color: "grey.700", fontSize: "1.3rem", fontWeight: 500 }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ fontSize: "1.3rem", py: 4, color: "text.secondary" }}>
                    No recent transactions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={transactions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "1.3rem",
              },
              "& .MuiTablePagination-select": {
                fontSize: "1.3rem",
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1.5rem",
              },
            }}
          />
        </Box>
      </Box>
    </Wrapper>
  );
}
